import { Component, DestroyRef, ElementRef, EventEmitter, HostListener, OnInit, Output, ViewChild, inject } from "@angular/core";
import { NgClass } from "@angular/common";
import { NavigationEnd, Router } from "@angular/router";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { debounceTime, filter } from "rxjs/operators";
import { NzLayoutModule } from "ng-zorro-antd/layout";
import { NzMenuModule } from "ng-zorro-antd/menu";
import { Menu } from "../../models/sidebar-menu.interface";
import { LocalStorageService } from "../../services/local-storage.service";
import { MenuService } from "../../services/menu.service";
import { SvgIconComponent } from "../svg-icon/svg-icon.component";
import { CommonUtils } from "../../utils/common-utils";

// Computed once at module load — not on every keydown event.
const IS_MAC = /mac/i.test((navigator as any).userAgentData?.platform ?? navigator.platform);
export const SIDENAV_COLLAPSED_WIDTH = 70;
export const SIDENAV_EXPANDED_WIDTH = 250;

interface SidebarNode {
  id: string;
  label: string;
  icon?: string;
  route?: string;
  children: SidebarNode[];
}

@Component({
  selector: "app-sidebar-v3",
  standalone: true,
  imports: [
    NgClass,
    NzLayoutModule,
    NzMenuModule,
    SvgIconComponent
  ],
  templateUrl: "./sidebar-v3.component.html",
  styleUrl: "./sidebar-v3.component.scss"
})
export class SidebarV3Component implements OnInit {

  private readonly destroyRef = inject(DestroyRef);
  private readonly elRef = inject(ElementRef);

  @Output() toggle = new EventEmitter<{ expanded: boolean; pinned: boolean; searching: boolean }>();

  @ViewChild('searchInput') private searchInputRef?: ElementRef<HTMLInputElement>;

  /** Keyboard shortcut label shown in the search placeholder, adjusted per platform. */
  readonly searchShortcutLabel = IS_MAC ? '⌘G' : 'Ctrl+G';

  // ─── Expansion / pin state ────────────────────────────────────────────────
  isExpanded = false;
  isPinned = false;
  isHovered = false;

  // ─── Menu state ───────────────────────────────────────────────────────────
  navigationItems: SidebarNode[] = [];
  /**
   * IDs of submenus currently open. Intentionally persists across collapse/expand
   * so the sidebar restores its open state instantly on hover without a re-sync.
   */
  openNodeIds = new Set<string>();
  activeRoute = "";
  searchQuery = "";

  /**
   * ID of the menu item currently highlighted by keyboard navigation.
   * The DOM focus stays in the search input; this drives a CSS highlight class.
   */
  focusedNodeId: string | null = null;

  /**
   * When the sidebar is pinned, keyboard navigation only activates after the
   * user clicks inside the sidebar. Tracks that click-to-activate state.
   */
  private isSidebarFocused = false;

  /** Pending timer that expands the sidebar after the cursor lingers. */
  private expandTimeoutRef: ReturnType<typeof setTimeout> | null = null;

  /** Pending timer that collapses the sidebar after the cursor leaves. */
  private collapseTimeoutRef: ReturnType<typeof setTimeout> | null = null;

  /**
   * Bidirectional lookup tables built when the navigation tree is constructed.
   * Used by openAncestorNodes and closeDescendantOpenNodes.
   */
  private parentNodeIdByNodeId = new Map<string, string | null>();
  private childNodeIdsByParentId = new Map<string | null, string[]>();

  get collapsedWidth(): number {
    return SIDENAV_COLLAPSED_WIDTH;
  }

  get expandedWidth(): number {
    return SIDENAV_EXPANDED_WIDTH;
  }

  get filteredNavigationItems(): SidebarNode[] {
    const q = this.searchQuery.trim().toLowerCase();
    if (!q) return this.navigationItems;
    return this.filterTree(this.navigationItems, q);
  }

  constructor(
    private localStorageService: LocalStorageService,
    private menuService: MenuService,
    private router: Router
  ) {
    // Clean up the collapse timer on destroy without needing ngOnDestroy.
    this.destroyRef.onDestroy(() => {
      this.clearExpandTimer();
      this.clearCollapseTimer();
    });
  }

  ngOnInit(): void {
    this.loadSidebarState();

    this.menuService.getMenuItems()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(items => {
        this.navigationItems = this.buildNavigationTree(items ?? []);
        this.syncMenuStateWithRoute();
      });

    // Clear search and re-sync active item highlight on every navigation.
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        debounceTime(100),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.searchQuery = "";
        this.syncMenuStateWithRoute();
      });
  }

  // ─── Global keyboard / click handlers ────────────────────────────────────

  /**
   * Tracks whether the user has clicked inside the sidebar.
   * When pinned, keyboard navigation is gated behind this so the sidebar
   * doesn't steal arrow keys while the user works elsewhere on the page.
   */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    this.isSidebarFocused = this.elRef.nativeElement.contains(event.target as Node);
  }

  @HostListener('document:keydown', ['$event'])
  onGlobalKeydown(event: KeyboardEvent): void {
    // Cmd+G / Ctrl+G: expand sidebar, focus search, and activate keyboard mode.
    const isOpenShortcut = IS_MAC
      ? event.metaKey && event.key === 'g'
      : event.ctrlKey && event.key === 'g';

    if (isOpenShortcut) {
      event.preventDefault();
      if (!this.isExpanded) {
        this.isExpanded = true;
        this.emitToggle();
      }
      this.isSidebarFocused = true;
      // Defer by one tick so the search input is guaranteed to be in the DOM.
      setTimeout(() => this.searchInputRef?.nativeElement.focus(), 0);
      return;
    }

    // Navigation keys are only active while the sidebar is expanded.
    if (!this.isExpanded) return;

    // When pinned, require an explicit click inside the sidebar first so
    // arrow keys don't interfere with the rest of the page.
    if (this.isPinned && !this.isSidebarFocused) return;

    // Don't intercept if the user is typing in an unrelated input on the page.
    const activeEl = document.activeElement;
    const isOwnSearch = activeEl === this.searchInputRef?.nativeElement;
    const isExternalInput = !isOwnSearch && (
      activeEl instanceof HTMLInputElement ||
      activeEl instanceof HTMLTextAreaElement ||
      activeEl instanceof HTMLSelectElement
    );
    if (isExternalInput) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.moveFocus(1);
        break;

      case 'ArrowUp':
        event.preventDefault();
        this.moveFocus(-1);
        break;

      case 'Enter':
        if (this.focusedNodeId) {
          event.preventDefault();
          this.activateFocusedNode();
        }
        break;

      case 'ArrowRight': {
        if (!this.focusedNodeId) break;
        event.preventDefault();
        const node = this.findNodeById(this.filteredNavigationItems, this.focusedNodeId);
        if (node?.children.length && !this.openNodeIds.has(this.focusedNodeId)) {
          this.openNodeIds.add(this.focusedNodeId);
        }
        break;
      }

      case 'ArrowLeft': {
        if (!this.focusedNodeId) break;
        event.preventDefault();
        const node = this.findNodeById(this.filteredNavigationItems, this.focusedNodeId);
        if (node?.children.length && this.openNodeIds.has(this.focusedNodeId)) {
          this.openNodeIds.delete(this.focusedNodeId);
          this.closeDescendantOpenNodes(this.focusedNodeId);
        } else {
          const parentId = this.parentNodeIdByNodeId.get(this.focusedNodeId) ?? null;
          if (parentId) {
            this.focusedNodeId = parentId;
            this.scrollFocusedNodeIntoView();
          }
        }
        break;
      }

      case 'Escape':
        if (this.searchQuery) {
          this.clearSearch();
        } else if (this.focusedNodeId) {
          this.focusedNodeId = null;
        } else if (!this.isPinned) {
          this.isExpanded = false;
          this.openNodeIds.clear();
          this.emitToggle();
          this.searchInputRef?.nativeElement.blur();
        }
        break;
    }
  }

  // ─── Mouse interaction ─────────────────────────────────────────────────────

  onSidebarMouseEnter(): void {
    this.isHovered = true;
    this.clearCollapseTimer();

    if (this.isPinned || this.isExpanded) {
      return;
    }

    // Delay opening so a rapid pass-through doesn't flash the sidebar open.
    this.expandTimeoutRef = setTimeout(() => {
      if (!this.isHovered || this.isPinned || this.isExpanded) return;
      this.isExpanded = true;
      this.syncMenuStateWithRoute();
      this.emitToggle();
    }, 300);
  }

  onSidebarMouseLeave(): void {
    this.isHovered = false;
    this.clearExpandTimer();
    this.clearCollapseTimer();

    if (this.isPinned || !this.isExpanded) {
      return;
    }

    // Small delay prevents a flicker when the cursor briefly exits the sidebar
    // (e.g. when moving toward a tooltip or a submenu near the boundary).
    this.collapseTimeoutRef = setTimeout(() => {
      if (this.isPinned || this.isHovered || !this.isExpanded) {
        return;
      }

      this.isExpanded = false;
      this.searchQuery = "";
      this.openNodeIds.clear();
      this.emitToggle();
    }, 120);
  }

  togglePin(_event: MouseEvent): void {
    this.isPinned = !this.isPinned;
    this.isExpanded = this.isPinned || this.isHovered;

    if (!this.isExpanded) {
      this.searchQuery = "";
    }

    this.syncMenuStateWithRoute();
    this.emitToggle();
    this.saveSidebarState();
  }

  // ─── Navigation ───────────────────────────────────────────────────────────

  navigateTo(route?: string): void {
    if (!route) {
      return;
    }
    this.focusedNodeId = null;
    this.router.navigateByUrl(route);
    this.searchInputRef?.nativeElement.blur();
    if (!this.isPinned && this.searchQuery !== '') {
      this.isExpanded = false;
      this.searchQuery = "";
      this.openNodeIds.clear();
      this.emitToggle();
    }
  }

  isRouteSelected(route?: string): boolean {
    return this.isRouteMatch(this.activeRoute, route);
  }

  /**
   * Returns true only when the sidebar is expanded. Submenus are always visually
   * closed in the narrow collapsed state, but openNodeIds is preserved so they
   * reopen instantly on hover without needing a re-sync.
   */
  isNodeOpen(nodeId: string): boolean {
    return this.isExpanded && this.openNodeIds.has(nodeId);
  }

  // ─── Search ───────────────────────────────────────────────────────────────

  onSearchChange(query: string): void {
    this.searchQuery = query;
    this.focusedNodeId = null;
    const q = query.trim().toLowerCase();

    if (!q) {
      this.syncMenuStateWithRoute();
      return;
    }

    // Auto-expand every parent node that has matching descendants.
    const expanded = new Set<string>();
    this.collectParentNodeIds(this.filteredNavigationItems, expanded);
    this.openNodeIds = expanded;
  }

  clearSearch(): void {
    this.searchQuery = "";
    this.focusedNodeId = null;
    this.syncMenuStateWithRoute();
  }

  // ─── Submenu open/close ───────────────────────────────────────────────────

  onSubmenuOpenChange(nodeId: string, isOpen: boolean): void {
    if (!this.isExpanded) {
      return;
    }

    // ng-zorro fires nzOpenChange for programmatic [nzOpen] binding changes too,
    // not only user clicks. If the value already matches our state, it's a
    // programmatic echo — ignore it to avoid feedback loops.
    if (this.openNodeIds.has(nodeId) === isOpen) {
      return;
    }

    if (isOpen) {
      this.openNodeIds.add(nodeId);
      this.openAncestorNodes(nodeId);
      return;
    }

    this.openNodeIds.delete(nodeId);
    this.closeDescendantOpenNodes(nodeId);
  }

  // ─── Private: keyboard navigation helpers ─────────────────────────────────

  /**
   * Moves the keyboard highlight by `direction` (+1 down, -1 up) through the
   * flat list of currently visible nodes. Wraps around at both ends.
   */
  private moveFocus(direction: 1 | -1): void {
    const nodes = this.getFlatVisibleNodes();
    if (nodes.length === 0) return;

    if (!this.focusedNodeId) {
      this.focusedNodeId = direction === 1 ? nodes[0].id : nodes[nodes.length - 1].id;
    } else {
      const currentIndex = nodes.findIndex(n => n.id === this.focusedNodeId);
      const nextIndex = (currentIndex + direction + nodes.length) % nodes.length;
      this.focusedNodeId = nodes[nextIndex].id;
    }

    this.scrollFocusedNodeIntoView();
  }

  /**
   * Activates the currently highlighted node: navigates if it's a leaf,
   * or toggles the submenu open/close if it's a parent.
   */
  private activateFocusedNode(): void {
    if (!this.focusedNodeId) return;

    const node = this.findNodeById(this.filteredNavigationItems, this.focusedNodeId);
    if (!node) return;

    if (node.children.length > 0) {
      if (this.openNodeIds.has(node.id)) {
        this.openNodeIds.delete(node.id);
        this.closeDescendantOpenNodes(node.id);
      } else {
        this.openNodeIds.add(node.id);
      }
    } else if (node.route) {
      this.navigateTo(node.route);
    }
  }

  /**
   * Returns all nodes currently visible in the menu in top-to-bottom order,
   * respecting which submenus are open.
   */
  private getFlatVisibleNodes(): SidebarNode[] {
    const result: SidebarNode[] = [];
    this.flattenVisibleNodes(this.filteredNavigationItems, result);
    return result;
  }

  private flattenVisibleNodes(nodes: SidebarNode[], result: SidebarNode[]): void {
    for (const node of nodes) {
      result.push(node);
      if (node.children.length > 0 && this.openNodeIds.has(node.id)) {
        this.flattenVisibleNodes(node.children, result);
      }
    }
  }

  private findNodeById(nodes: SidebarNode[], id: string): SidebarNode | null {
    for (const node of nodes) {
      if (node.id === id) return node;
      if (node.children.length > 0) {
        const found = this.findNodeById(node.children, id);
        if (found) return found;
      }
    }
    return null;
  }

  private scrollFocusedNodeIntoView(): void {
    if (!this.focusedNodeId) return;
    setTimeout(() => {
      const el = this.elRef.nativeElement.querySelector(`[data-node-id="${this.focusedNodeId}"]`);
      el?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }, 0);
  }

  // ─── Private: emit ────────────────────────────────────────────────────────

  private emitToggle(): void {
    this.toggle.emit({ expanded: this.isExpanded, pinned: this.isPinned, searching: !!this.searchQuery });
  }

  // ─── Private: tree filtering ──────────────────────────────────────────────

  private filterTree(nodes: SidebarNode[], query: string): SidebarNode[] {
    const result: SidebarNode[] = [];
    for (const node of nodes) {
      if (CommonUtils.fuzzyMatch(node.label.toLowerCase(), query)) {
        // Node itself matches — include its entire subtree unchanged.
        result.push(node);
      } else {
        // Keep the node only if a descendant matches, trimming non-matching branches.
        const filteredChildren = this.filterTree(node.children, query);
        if (filteredChildren.length > 0) {
          result.push({ ...node, children: filteredChildren });
        }
      }
    }
    return result;
  }

  private collectParentNodeIds(nodes: SidebarNode[], result: Set<string>): void {
    for (const node of nodes) {
      if (node.children.length > 0) {
        result.add(node.id);
        this.collectParentNodeIds(node.children, result);
      }
    }
  }

  // ─── Private: persistence ─────────────────────────────────────────────────

  private saveSidebarState(): void {
    this.localStorageService.setItem("isSidebarPinned", this.isPinned);
    this.localStorageService.setItem("isSidebarExpanded", this.isExpanded);
  }

  private loadSidebarState(): void {
    const isSidebarPinned = this.localStorageService.getItem<boolean>("isSidebarPinned");
    const isSidebarExpanded = this.localStorageService.getItem<boolean>("isSidebarExpanded");

    // Migration: older sessions stored only isSidebarExpanded, not isSidebarPinned.
    // Treat those as pinned so returning users keep their sidebar open.
    this.isPinned = isSidebarPinned === true || (isSidebarPinned == null && isSidebarExpanded === true);
    this.isExpanded = this.isPinned;

    this.emitToggle();
  }

  // ─── Private: navigation tree ─────────────────────────────────────────────

  private buildNavigationTree(menuItems: Menu[]): SidebarNode[] {
    this.parentNodeIdByNodeId.clear();
    this.childNodeIdsByParentId.clear();
    return menuItems.map((menu, index) => this.mapMenuToNode(menu, "root", index, null));
  }

  private mapMenuToNode(menu: Menu, parentPath: string, index: number, parentNodeId: string | null): SidebarNode {
    const nodeId = this.buildNodeId(parentPath, menu.id, index);
    this.registerNode(nodeId, parentNodeId);

    const menuChildren = menu.childResources ?? [];
    return {
      id: nodeId,
      label: menu.displayLabel || menu.name,
      icon: menu.icon,
      route: menu.resourceProperties?.url,
      children: this.mapChildNodes(nodeId, menuChildren)
    };
  }

  /**
   * Maps child menu items under a given parent, grouping leaf items that share
   * a `category` property into synthetic submenu nodes. Items with their own
   * children are never grouped — they keep their own submenu structure.
   */
  private mapChildNodes(parentNodeId: string, menuItems: Menu[]): SidebarNode[] {
    const nonGroupedNodes: SidebarNode[] = [];
    const groupedByCategory = new Map<string, Menu[]>();

    for (const [index, menu] of menuItems.entries()) {
      const hasChildren = (menu.childResources?.length ?? 0) > 0;
      const category = menu.resourceProperties?.category?.trim();

      if (category && !hasChildren) {
        const categoryMenus = groupedByCategory.get(category) ?? [];
        categoryMenus.push(menu);
        groupedByCategory.set(category, categoryMenus);
        continue;
      }

      nonGroupedNodes.push(this.mapMenuToNode(menu, parentNodeId, index, parentNodeId));
    }

    for (const [index, [category, categoryMenus]] of Array.from(groupedByCategory.entries()).entries()) {
      const categoryNodeId = this.buildNodeId(parentNodeId, category, index);
      this.registerNode(categoryNodeId, parentNodeId);

      const categoryNodes = categoryMenus.map((menu, categoryIndex) =>
        this.mapMenuToNode(menu, categoryNodeId, categoryIndex, categoryNodeId)
      );

      nonGroupedNodes.push({ id: categoryNodeId, label: category, children: categoryNodes });
    }

    return nonGroupedNodes;
  }

  private buildNodeId(parentPath: string, rawId: string, index: number): string {
    return `${parentPath}/${rawId}-${index}`;
  }

  private registerNode(nodeId: string, parentNodeId: string | null): void {
    this.parentNodeIdByNodeId.set(nodeId, parentNodeId);
    const childNodeIds = this.childNodeIdsByParentId.get(parentNodeId) ?? [];
    childNodeIds.push(nodeId);
    this.childNodeIdsByParentId.set(parentNodeId, childNodeIds);
  }

  // ─── Private: route sync ──────────────────────────────────────────────────

  private syncMenuStateWithRoute(): void {
    this.activeRoute = this.router.url.split("?")[0] ?? "";
    const pathToActiveRoute = this.findPathByRoute(this.navigationItems, this.activeRoute) ?? [];
    // Open all ancestors of the active leaf, but not the leaf itself.
    this.openNodeIds = new Set(pathToActiveRoute.slice(0, -1));
    this.focusedNodeId = null;
  }

  private findPathByRoute(nodes: SidebarNode[], route: string, currentPath: string[] = []): string[] | null {
    for (const node of nodes) {
      const nextPath = [...currentPath, node.id];

      if (this.isRouteMatch(route, node.route)) {
        return nextPath;
      }

      if (node.children.length > 0) {
        const nestedPath = this.findPathByRoute(node.children, route, nextPath);
        if (nestedPath) {
          return nestedPath;
        }
      }
    }
    return null;
  }

  private isRouteMatch(currentRoute: string, route?: string): boolean {
    if (!route) {
      return false;
    }
    return currentRoute === route;
  }

  // ─── Private: open/close helpers ──────────────────────────────────────────

  private openAncestorNodes(nodeId: string): void {
    let currentParent = this.parentNodeIdByNodeId.get(nodeId) ?? null;
    while (currentParent) {
      this.openNodeIds.add(currentParent);
      currentParent = this.parentNodeIdByNodeId.get(currentParent) ?? null;
    }
  }

  private closeDescendantOpenNodes(nodeId: string): void {
    const childNodeIds = this.childNodeIdsByParentId.get(nodeId) ?? [];
    for (const childNodeId of childNodeIds) {
      this.openNodeIds.delete(childNodeId);
      this.closeDescendantOpenNodes(childNodeId);
    }
  }

  private clearExpandTimer(): void {
    if (!this.expandTimeoutRef) return;
    clearTimeout(this.expandTimeoutRef);
    this.expandTimeoutRef = null;
  }

  private clearCollapseTimer(): void {
    if (!this.collapseTimeoutRef) {
      return;
    }
    clearTimeout(this.collapseTimeoutRef);
    this.collapseTimeoutRef = null;
  }
}
